import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { CalendarPlus, ArrowLeft, Send, CheckCircle2, Info, CalendarDays } from "lucide-react";

// Sélecteur date (calendrier français) + heure, renvoie "yyyy-MM-ddTHH:mm".
function DateTimeField({ value, onChange, testid }) {
  const datePart = value ? value.slice(0, 10) : "";
  const timePart = value ? value.slice(11, 16) : "";
  const selected = datePart ? parse(datePart, "yyyy-MM-dd", new Date()) : undefined;
  const setDate = (d) => { if (!d) { onChange(""); return; } onChange(`${format(d, "yyyy-MM-dd")}T${timePart || "10:00"}`); };
  const setTime = (t) => { const ds = datePart || format(new Date(), "yyyy-MM-dd"); onChange(`${ds}T${t || "10:00"}`); };
  return (
    <div className="flex gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="flex-1 justify-start font-normal" data-testid={`${testid}-date`}>
            <CalendarDays size={14} className="mr-2 text-slate-500" />
            {datePart ? format(selected, "d MMM yyyy", { locale: fr }) : <span className="text-slate-500">Choisir une date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={fr} selected={selected} onSelect={setDate} initialFocus />
        </PopoverContent>
      </Popover>
      <Input type="time" value={timePart} onChange={(e) => setTime(e.target.value)} className="w-28" data-testid={`${testid}-time`} />
    </div>
  );
}

// Catégories d'événements grand public (on exclut « pro » réservé aux entreprises).
const CATS = [
  { v: "concert", l: "Concert" }, { v: "spectacle", l: "Spectacle" },
  { v: "marche", l: "Marché" }, { v: "animation", l: "Animation" },
  { v: "sport", l: "Sport" }, { v: "expo", l: "Exposition" },
  { v: "fete", l: "Fête" }, { v: "atelier", l: "Atelier" },
  { v: "autre", l: "Autre" },
];

const EMPTY = {
  title: "", description: "", start_date: "", end_date: "",
  city: "", address: "", category: "animation", image: "", url: "", organizer: "",
};

export default function ProposeEvent() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || form.title.trim().length < 3) { toast.error("Indiquez un titre (3 caractères minimum)."); return; }
    if (!form.start_date) { toast.error("Indiquez la date de début."); return; }
    if (!form.city || form.city.trim().length < 2) { toast.error("Indiquez la ville."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : "",
      };
      const { data } = await api.post("/events/propose", payload);
      toast.success(data?.message || "Proposition envoyée !");
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 401) { toast.message("Connectez-vous pour proposer un événement."); navigate("/connexion"); }
      else toast.error(err?.response?.data?.detail || "Envoi impossible.");
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-testid="propose-event-success">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 grid place-items-center"><CheckCircle2 className="text-emerald-600" size={34} /></div>
        <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Proposition envoyée !</h1>
        <p className="mt-2 text-slate-500 text-sm">Merci ! Votre événement sera publié dans l'agenda « Sortir en Eure-et-Loir » dès qu'il aura été validé par notre équipe.</p>
        <div className="mt-6 flex flex-wrap gap-2 justify-center">
          <Button onClick={() => { setForm(EMPTY); setDone(false); }} variant="outline" data-testid="propose-event-again">Proposer un autre événement</Button>
          <Link to="/sortir"><Button className="btn-shine text-white" data-testid="propose-event-see-agenda">Voir l'agenda</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28" data-testid="propose-event-page">
      <Link to="/sortir" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638]"><ArrowLeft size={14} /> Retour à l'agenda</Link>
      <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
        <CalendarPlus className="text-[#E05638]" size={26} /> Proposer un événement
      </h1>
      <p className="mt-1 text-sm text-slate-500">Partagez un événement local. Il sera ajouté à l'agenda après validation par notre équipe.</p>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-5">
        <div>
          <Label>Titre de l'événement *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={200} placeholder="Ex. Brocante de printemps à Chartres" data-testid="propose-event-title" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Date & heure de début *</Label>
            <div className="mt-2"><DateTimeField value={form.start_date} onChange={(v) => set("start_date", v)} testid="propose-event-start" /></div>
          </div>
          <div>
            <Label>Date & heure de fin (facultatif)</Label>
            <div className="mt-2"><DateTimeField value={form.end_date} onChange={(v) => set("end_date", v)} testid="propose-event-end" /></div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Ville *</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Chartres" data-testid="propose-event-city" />
          </div>
          <div>
            <Label>Catégorie *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="propose-event-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Adresse / lieu (facultatif)</Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Place des Épars, 28000 Chartres" data-testid="propose-event-address" />
        </div>

        <div>
          <Label>Description</Label>
          <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={5000}
                    placeholder="Décrivez l'événement : programme, horaires, tarif, public concerné…" data-testid="propose-event-desc" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Image (URL, facultatif)</Label>
            <Input value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" data-testid="propose-event-image" />
          </div>
          <div>
            <Label>Lien d'info / billetterie (facultatif)</Label>
            <Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" data-testid="propose-event-url" />
          </div>
        </div>

        <div>
          <Label>Organisateur (facultatif)</Label>
          <Input value={form.organizer} onChange={(e) => set("organizer", e.target.value)} placeholder="Nom de l'association, de la mairie…" data-testid="propose-event-organizer" />
        </div>

        <p className="text-xs text-slate-500 flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> Votre proposition est vérifiée avant publication. Merci de proposer uniquement des événements réels et ouverts au public en Eure-et-Loir.</p>

        <Button type="submit" disabled={saving} className="btn-shine text-white w-full" data-testid="propose-event-submit">
          <Send size={16} className="mr-2" /> {saving ? "Envoi…" : "Envoyer ma proposition"}
        </Button>
      </form>
    </div>
  );
}
